import { Alert, Badge, Group, Stack, Table, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { RETENTION_FLOORS } from '@prooffleet/contracts';
import { api } from '@prooffleet/client';
import { PageHeader, TableSkeleton } from '../components/Page.js';

interface PolicyRow {
  subject: string;
  days: number;
}

interface PlanRow {
  subject: string;
  eligible: number;
  blockedByLegalHold: number;
  cutoff: string;
  days: number;
}

interface ExportRow {
  _id: string;
  unitNumber: string;
  exportedByRole: string;
  createdAt: string;
  packageSha256: string;
  packageBytes: number;
  fileCount: number;
  auditChainVerified: boolean;
}

const SUBJECT_LABEL: Record<string, string> = {
  inspections: 'Inspection records',
  missed_inspections: 'Missed-inspection log',
  notifications: 'Notifications',
};

/**
 * Хранение и передачи.
 *
 * Экран сознательно только читающий: удаление данных — разрушающая операция,
 * которая требует явного подтверждения и основания, и запускать её той же
 * формой, что показывает план, нельзя. Сама операция пока доступна только
 * через API.
 */
export function Retention(): React.JSX.Element {
  const { data: policy, isLoading } = useQuery({
    queryKey: ['retention'],
    queryFn: () => api<{ policies: PolicyRow[] }>('/retention'),
  });
  const { data: plan } = useQuery({
    queryKey: ['retention', 'plan'],
    queryFn: () => api<{ items: PlanRow[] }>('/retention/plan'),
  });
  const { data: exports } = useQuery({
    queryKey: ['evidence-exports'],
    queryFn: () => api<{ exports: ExportRow[] }>('/evidence-exports'),
  });

  const policies = policy?.policies ?? [];
  const planned = new Map((plan?.items ?? []).map((i) => [i.subject, i]));
  const held = (plan?.items ?? []).reduce((n, i) => n + i.blockedByLegalHold, 0);

  return (
    <Stack gap="lg">
      <div>
        <PageHeader
          title="Retention"
          hint="A retention period can never be shorter than the law requires. A legal hold overrides deletion unconditionally."
        />

        {held > 0 && (
          <Alert color="orange" mb="md">
            <span className="pf-num">{held}</span> records are past their retention period but
            protected by a legal hold. They will not be deleted while the hold stands.
          </Alert>
        )}

        {isLoading && <TableSkeleton rows={3} cols={4} />}

        {!isLoading && (
          <Table.ScrollContainer minWidth={720}>
            <Table className="pf-table" layout="fixed">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w="30%">Data</Table.Th>
                  <Table.Th w="16%">Kept for</Table.Th>
                  <Table.Th w="20%">Legal minimum</Table.Th>
                  <Table.Th w="17%">Eligible now</Table.Th>
                  <Table.Th w="17%">Held</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {policies.map((row) => {
                  const floor = RETENTION_FLOORS[row.subject as keyof typeof RETENTION_FLOORS];
                  const item = planned.get(row.subject);
                  return (
                    <Table.Tr key={row.subject}>
                      <Table.Td>
                        <Text fz="sm" fw={560}>
                          {SUBJECT_LABEL[row.subject] ?? row.subject}
                        </Text>
                      </Table.Td>
                      <Table.Td className="pf-num">
                        <Text fz="sm">{row.days} days</Text>
                      </Table.Td>
                      <Table.Td>
                        {floor === undefined || floor.citation === null ? (
                          <Text fz="sm" c="dimmed">
                            —
                          </Text>
                        ) : (
                          <Group gap={6} wrap="nowrap">
                            <Text className="pf-num" fz="sm">
                              {floor.minDays} days
                            </Text>
                            <Badge size="xs" variant="light" color="gray">
                              {floor.citation.replace('49 CFR ', '')}
                            </Badge>
                          </Group>
                        )}
                      </Table.Td>
                      <Table.Td className="pf-num">
                        <Text fz="sm">{item?.eligible ?? 0}</Text>
                      </Table.Td>
                      <Table.Td className="pf-num">
                        <Text fz="sm" c={(item?.blockedByLegalHold ?? 0) > 0 ? 'orange.8' : 'dimmed'}>
                          {item?.blockedByLegalHold ?? 0}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </div>

      <div>
        <Text fw={620} fz="md" mb={2}>
          Chain of custody
        </Text>
        <Text c="dimmed" fz="sm" mb="sm" maw="68ch">
          Every evidence package that left the system, with the hash of the exact archive the
          recipient received.
        </Text>

        {(exports?.exports ?? []).length === 0 ? (
          <Text c="dimmed" fz="sm">
            No packages exported yet.
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={720}>
            <Table className="pf-table" layout="fixed">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w="16%">Unit</Table.Th>
                  <Table.Th w="20%">Exported by</Table.Th>
                  <Table.Th w="14%">Files</Table.Th>
                  <Table.Th w="26%">Archive hash</Table.Th>
                  <Table.Th w="24%">When</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(exports?.exports ?? []).map((row) => (
                  <Table.Tr key={row._id}>
                    <Table.Td>
                      <Text fz="sm" fw={560}>
                        {row.unitNumber}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="sm" c="gray.7">
                        {row.exportedByRole.replace('_', ' ')}
                      </Text>
                    </Table.Td>
                    <Table.Td className="pf-num">
                      <Text fz="sm">{row.fileCount}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text className="pf-mono" truncate>
                        {row.packageSha256.slice(0, 16)}…
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text className="pf-mono">{new Date(row.createdAt).toLocaleString()}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </div>
    </Stack>
  );
}
